import React, { useState, useEffect } from 'react'
import { Popover } from '@manychat/manyui'

import { SliceType } from 'components/MCTextEditor/slice/types'
import { Decorators } from 'components/RichTextEditor/decorators/useDecorators'

import { Tools } from '../toolbar/toolbarInterfaces'
import { IToolHandler } from '../types'

import SimpleDecoratorTool from './items/SimpleDecoratorTool'
import TextColorTool from './items/TextColorTool'
import ToggleTool from './items/ToggleTool'

import cm from './FormattingToolbar.module.css'

const stopPropagationFn = (e: React.MouseEvent<HTMLElement>) => {
  e.stopPropagation()
  e.preventDefault()
}

export interface IProps {
  open: boolean
  onInsert: IToolHandler
  decorators: Decorators
  anchor?: React.MutableRefObject<HTMLDivElement | HTMLTextAreaElement | null>
  blurEditor: () => void
  focusEditor: () => void
}

const FormattingToolbar = ({
  open,
  onInsert,
  anchor,
  decorators,
  blurEditor,
  focusEditor,
}: IProps) => {
  const [selectedTool, selectTool] = useState<string | null>(null)
  const [isColorPickerOpen, setColorPickerOpen] = useState(false)

  useEffect(() => {
    if (open) {
      selectTool(null)
    }
  }, [open])

  const [isMouseOver, setIsMouseOver] = React.useState(false)
  const handleMouseOver = React.useCallback(() => setIsMouseOver(true), [])
  const handleMouseOut = React.useCallback(() => setIsMouseOver(false), [])

  const handleInsert: IToolHandler = React.useCallback(
    (value, selectionOffset) => {
      selectTool(null)
      onInsert(value, selectionOffset)
    },
    [onInsert],
  )

  const onColorPickerOpenChange = (open: boolean) => {
    setColorPickerOpen(open)
    if (!open) {
      focusEditor()
    }
  }

  return (
    <Popover
      open={open || !!selectedTool || isColorPickerOpen || isMouseOver}
      anchor={anchor?.current}
      anchorOrigin="center bottom"
      popoverOrigin="center top-20px"
      autoPosition="none keep_at_screen"
      useLayerForClickAway={false}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={stopPropagationFn}
      onMouseDown={stopPropagationFn}
      className={cm.popover}
      onRequestClose={(cause: string) => {
        if (cause === 'offScreen') {
          blurEditor()
        }
      }}
    >
      <div className="d-flex flex-row align-center" style={{ height: '48px' }}>
        <ToggleTool
          tool={Tools.smartLinks}
          className="m-l"
          selectedTool={selectedTool}
          onSelect={selectTool}
          onInsert={handleInsert}
        />
        <SimpleDecoratorTool
          decorators={decorators}
          decoratorType={SliceType.BOLD}
          className="m-l"
        />
        <SimpleDecoratorTool
          decorators={decorators}
          decoratorType={SliceType.ITALIC}
          className="m-l"
        />
        <SimpleDecoratorTool
          decorators={decorators}
          decoratorType={SliceType.UNDERLINE}
          className="m-l"
        />
        <TextColorTool
          decorators={decorators}
          onOpenChange={onColorPickerOpenChange}
          className="m-x"
        />
        <div className="b-r" style={{ height: '100%' }} />
        <ToggleTool
          tool={Tools.vars}
          className="m-l"
          selectedTool={selectedTool}
          onSelect={selectTool}
          onInsert={handleInsert}
        />
        <ToggleTool
          tool={Tools.emoji}
          className="m-x"
          selectedTool={selectedTool}
          onSelect={selectTool}
          onInsert={handleInsert}
        />
      </div>
    </Popover>
  )
}

export default FormattingToolbar
